import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendFeedback } from '../../../api/productService';
import { useToast } from '../../../hooks/useToast';
import './Feedback.css';
import CopyrightText from '../CopyrightText/CopyrightText';

const MAX_CHARS = 150;

const Feedback: React.FC = () => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  const handleRating = (rate: number) => {
    setRating(rate);
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_CHARS) {
      setFeedback(e.target.value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0 && feedback.trim() === '') {
      showError(t('product.feedback.ratingOrFeedbackRequired'));
      return;
    }

    setLoading(true);

    try {
      await sendFeedback(rating, feedback);
      showSuccess(t('product.feedback.success'));

      // Navigate back after showing success message
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (error: any) {
      console.error('Error sending feedback:', error);

      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          t('product.feedback.error');

      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feedback-page">
      <div className="feedback-content">
        <div className="feedback-card">
          <h2 className="feedback-title">{t('product.feedback.question')}</h2>

          <div className="feedback-rating">
            <div className="feedback-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`feedback-star ${rating >= star ? 'selected' : ''}`}
                  onClick={() => handleRating(star)}
                  width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        stroke={rating >= star ? '#556BFF' : '#B0B3B9'} strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        fill={rating >= star ? '#556BFF' : 'none'}
                  />
                </svg>
              ))}
            </div>
            <div className="feedback-rating-labels">
              <span>{t('product.feedback.notSatisfied')}</span>
              <span>{t('product.feedback.verySatisfied')}</span>
            </div>
          </div>

          <div className="feedback-textarea-container">
            <textarea
              placeholder={t('product.feedback.placeholder')}
              value={feedback}
              onChange={handleFeedbackChange}
              className="feedback-textarea"
              disabled={loading}
            />
            <div className="feedback-char-counter">
              {feedback.length}/{MAX_CHARS}
            </div>
          </div>
        </div>
      </div>
      <div className="feedback-footer">
          <button
            type="submit"
            className="feedback-submit"
            onClick={handleSubmit}
            disabled={(rating === 0 && feedback.trim() === '') || loading}
          >
            {loading ? t('common.loading') : t('product.feedback.submit')}
          </button>
      </div>
      <CopyrightText />
    </div>
  );
};

export default Feedback;
